// useAdminLotBidLedger — the clerk console bid record's source (BUG-1467).
//
// A HOOK, not a provider, and that is the whole design. The auction-wide ledger
// is a page-level provider because every consumer wants the same one auction.
// This feed is per SELECTED LOT: it must attach when a clerk selects a lot and
// let go when they select another, so it is scoped to the component that renders
// the selection rather than to the page.
//
// WHY IT EXISTS. The auction-wide de-anon ledger cold-seeds only the newest
// `CATCHUP_BID_CAP` (2,000) bids, and the bid record folded that stream and
// filtered it to the selected lot. On a sale past the cap, a lot whose bidding
// finished earlier had NONE of its bids in the window, so the filter yielded
// `[]` and the pane rendered "No bids on this lot" — beside a workspace showing
// the lot's hammer price off uncapped columns. Truncation was indistinguishable
// from a genuinely quiet lot, at every layer: the route returned 200, the read
// succeeded, and the UI rendered a legitimate-looking empty state.
//
// NO NEW MACHINERY. `LedgerStore` already ref-counts subscribers per topic and
// runs `initialFetch` once for a topic's first subscriber, so switching lots is
// just switching topics: the previous lot's entry drops to zero subscribers and
// unsubscribes itself. The only new thing is the topic, which the server mints.

'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    decodeAdminBidLedgerEntry,
    type AdminBidLedgerCatchupResult,
    type AdminBidLedgerEntry,
    type AdminBidLedgerEntryDecoded,
} from '../../mappers/realtime/admin-bid-ledger';
import { useLedger } from '../../components/realtime/LedgerContext';
import type { LedgerCursor } from '../../stores/realtime/ledger-store';
import { formatRealtimeFailure } from '../../isomorphic/realtime/realtime-failure-log';

/**
 * Shown when a feed backing the bid record cannot be read.
 *
 * ONE spelling for the condition, deliberately — the clerk should not be able to
 * tell WHICH feed failed, and two spellings of one condition is how copy drifts.
 * Exported since BL-1552, when the per-lot max-bid feed became a second way for
 * the same pane to be unreadable.
 */
export const BID_RECORD_FEED_ERROR_MESSAGE = 'Bid activity is unavailable right now.';

/**
 * Authenticated per-lot catch-up fetcher: mints the obscured topic and returns
 * the lot's bids since the cursor. MUST be a stable reference — the bootstrap
 * effect keys on it, so a fresh closure each render would re-bootstrap forever.
 */
export type AdminLotBidLedgerCatchupFetcher = (
    lotId: string,
    cursor: LedgerCursor | null,
) => Promise<AdminBidLedgerCatchupResult>;

export interface AdminLotBidLedgerValue {
    /** The lot's bids, ascending by the per-lot sequence (BL-1631; was
     *  `(createdAt, id)`). Never truncated. Each amount is already a `Money`
     *  in the bid's own currency — decoded once here, so no component
     *  re-parses the wire value (BL-1942). */
    entries: readonly AdminBidLedgerEntryDecoded[];
    /**
     * Bootstrap failure, or `null`.
     *
     * Load-bearing: the pane MUST be able to tell "this lot has had no bids"
     * from "I could not read this lot's bids". Conflating them is BUG-1467's
     * failure mode, and an unavailable feed rendering as a confident empty state
     * is what let a clerk read a wrong answer back to a buyer.
     */
    error: string | null;
}

interface BootstrapState {
    lotId: string;
    topic: string | null;
    error: string | null;
}

/**
 * Subscribe to one lot's de-anon bid ledger, cold-seeding its full history.
 *
 * Positional, mirroring its sibling `useLedger(topic, ...)`: the thing being
 * subscribed to leads, the plumbing follows.
 *
 * @param lotId - the selected lot. `null` means "nothing selected" — no
 *   subscription, and empty entries.
 * @param catchupFetcher - stable catch-up fetcher (see
 *   {@link AdminLotBidLedgerCatchupFetcher}). MUST be a stable reference: the
 *   bootstrap effect keys on it, so a fresh closure each render re-bootstraps
 *   forever.
 * @returns The lot's entries plus a bootstrap error, if any.
 * @sideEffect Issues ONE `GET /api/realtime/admin_lot_bid_ledger/catchup` per
 *   selected lot to learn the obscured topic, then subscribes to it via the
 *   shared `LedgerStore`. Logs (once per row) an entry whose amount is not a
 *   well-formed MoneyWire, and leaves it out rather than crashing the pane.
 */
export function useAdminLotBidLedger(
    lotId: string | null,
    catchupFetcher: AdminLotBidLedgerCatchupFetcher,
): AdminLotBidLedgerValue {
    // ONE state cell carrying the lot it belongs to, so switching lots
    // invalidates the previous bootstrap by comparison rather than by a
    // synchronous `setState` in the effect body (which would cascade a render).
    const [boot, setBoot] = useState<BootstrapState | null>(null);
    // The bootstrap's items, held until the store's first subscriber claims
    // them. A ref (not state) because consuming the seed must not re-render.
    const seedRef = useRef<AdminBidLedgerEntry[] | null>(null);

    useEffect(() => {
        if (lotId == null) return;
        let cancelled = false;
        seedRef.current = null;

        catchupFetcher(lotId, null)
            .then((result) => {
                if (cancelled) return;
                seedRef.current = result.items;
                setBoot({ lotId, topic: result.topic, error: null });
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                // `console.error`, NOT `createLogger`: `createLogger` is
                // server-only (ALS / `node:async_hooks`) and cannot be bundled
                // for the browser.
                // eslint-disable-next-line no-console
                console.error(
                    ...formatRealtimeFailure(
                        '[useAdminLotBidLedger] bootstrap failed',
                        { lotId },
                        err,
                    ),
                );
                setBoot({ lotId, topic: null, error: BID_RECORD_FEED_ERROR_MESSAGE });
            });

        return () => {
            cancelled = true;
        };
    }, [lotId, catchupFetcher]);

    // A bootstrap for a DIFFERENT lot is stale the moment `lotId` changes —
    // treated as absent rather than briefly rendered under the new lot's header
    // (RAC 5: no previous lot's bids under a new lot).
    const current = boot != null && boot.lotId === lotId ? boot : null;
    const topic = current?.topic ?? null;
    const error = current?.error ?? null;
    const hasTopic = topic !== null;

    // Consume the bootstrap's items as the seed. Cleared on read so a later
    // remount on the same topic re-fetches rather than replaying a stale set.
    const initialFetch = useCallback(async (): Promise<AdminBidLedgerEntry[]> => {
        if (!hasTopic || lotId == null) return [];
        const seeded = seedRef.current;
        if (seeded) {
            seedRef.current = null;
            return seeded;
        }
        const result = await catchupFetcher(lotId, null);
        return result.items;
    }, [lotId, hasTopic, catchupFetcher]);

    const reconcile = useCallback(
        async (cursor: LedgerCursor | undefined): Promise<AdminBidLedgerEntry[]> => {
            if (!hasTopic || lotId == null) return [];
            const result = await catchupFetcher(lotId, cursor ?? null);
            return result.items;
        },
        [lotId, hasTopic, catchupFetcher],
    );

    // `''` is the store's inert topic — accepted, never subscribed. That is the
    // state before the bootstrap lands and whenever no lot is selected.
    const wireEntries = useLedger<AdminBidLedgerEntry>(topic ?? '', {
        initialFetch,
        reconcile,
    });
    // The store keeps the wire shape (it dedupes and orders; it does not
    // decode). Decoding is the hook boundary, once per ledger change.
    const loggedUndecodable = useRef(new Set<string>());
    const { entries, undecodable } = useMemo(() => {
        const out: AdminBidLedgerEntryDecoded[] = [];
        const bad: AdminBidLedgerEntry[] = [];
        for (const wire of wireEntries) {
            const result = decodeAdminBidLedgerEntry(wire);
            if (result.kind === 'ok') out.push(result.entry);
            // Backstop: a row whose amount is not a well-formed MoneyWire is
            // left out, never thrown, so one bad row cannot take down the
            // clerk's record.
            else bad.push(wire);
        }
        return { entries: out, undecodable: bad };
    }, [wireEntries]);

    // Logged from an effect, not the memo (a memo must stay pure), once per bid id.
    useEffect(() => {
        for (const wire of undecodable) {
            if (loggedUndecodable.current.has(wire.id)) continue;
            loggedUndecodable.current.add(wire.id);
            // eslint-disable-next-line no-console
            console.error(
                ...formatRealtimeFailure(
                    '[useAdminLotBidLedger] undecodable bid amount — row skipped',
                    { lotId, bidId: wire.id },
                    new Error(`unrecognised amount shape: ${JSON.stringify(wire.amount)}`),
                ),
            );
        }
    }, [undecodable, lotId]);

    return { entries, error };
}
